import { CommonModule, DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  IonApp,
  IonButton,
  IonIcon,
  IonInput,
  IonRouterOutlet,
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing.module';
import { BannerComponent } from './components/banner/banner.component';
import { PackageModule } from './components/package/package.module';
import { ReportsModule } from './components/reports/reports.module';
import { EditAccessPointComponent } from './components/shared/modal/edit-access-point/edit-access-point.component';
import { WarningModalComponent } from './components/shared/modal/warning-modal/warning-modal.component';
import { FilterByCodePipe } from './components/shared/pipes/filter-by-code.pipe';
import { SharedModule } from './components/shared/shared.module';
import { DisplayOnlyIconComponent } from './components/side-nav/display-only-icon/display-only-icon.component';
import { NavIconComponent } from './components/side-nav/nav-icon/nav-icon.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { SublinkIconComponent } from './components/side-nav/sublink-icon/sublink-icon.component';
import { TopNavComponent } from './components/top-nav/top-nav.component';
import { getMsalConfig } from './core/auth/auth-config';
import { OutgoingInterceptor } from './core/auth/outgoing.interceptor';
import { currentLocale } from './locale.config';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(getMsalConfig());
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiUrl, environment.B2E_scope);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

@NgModule({
  declarations: [
    AppComponent,
    WarningModalComponent,
    EditAccessPointComponent,
    TopNavComponent,
    SideNavComponent,
    NavIconComponent,
    SublinkIconComponent,
    DisplayOnlyIconComponent,
    BannerComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    SharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MatSortModule,
    MatTableModule,
    ReactiveFormsModule,
    CommonModule,
    MatSelectModule,
    RouterModule,
    MatMenuModule,
    FormsModule,
    MatButtonModule,
    MatTreeModule,
    MatRippleModule,
    MatIconModule,
    MatSidenavModule,
    MatDialogModule,
    MatTabsModule,
    MatInputModule,
    IonApp,
    IonRouterOutlet,
    MsalModule,
    PackageModule,
    IonIcon,
    IonButton,
    IonInput,
    FilterByCodePipe,
    ReportsModule,
  ],
  providers: [
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    HttpClient,
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular(),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: HTTP_INTERCEPTORS, useClass: OutgoingInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: LOCALE_ID,
      useValue: currentLocale(),
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
  ],
})
export class AppModule {}
